import { SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useOutletContext } from 'react-router'

import { useNotifications } from 'app/application/application'
import { PublicRoutes, SetupWorkspaceRoutes } from 'app/application/types'
import { ArrowLeftIcon, ArrowRightIcon } from 'app/common/presentation/icons'

import { Button, Input } from 'components'

import accessStyles from '../AccessPage/AccessPage.module.scss'
import { validatePassword } from '../validatePassword'
import { UserAccessContext, isCreateWorkspaceInput } from './CreateAccess'
import styles from './CreateAccess.module.scss'

const SetupPasswordPage = () => {
  const { te } = useNotifications()
  const { t } = useTranslation('user')

  const navigate = useNavigate()

  const { error, formInput, handleCreateWorkspace, setError, setFormInput } =
    useOutletContext<UserAccessContext>()

  const isCreateWorkspace = isCreateWorkspaceInput(formInput)
  const inviteSearch = new URLSearchParams({
    email: formInput.email,
    inviteCode: formInput.inviteCode,
  }).toString()

  if (isCreateWorkspace && !formInput.workspace.trim()) {
    return <Navigate to={`${PublicRoutes.CreateWorkspace}?${inviteSearch}`} replace />
  }

  if (!formInput.email || !formInput.firstName || !formInput.inviteCode || !formInput.lastName) {
    return (
      <Navigate
        to={
          isCreateWorkspace
            ? `${PublicRoutes.CreateWorkspace}/${SetupWorkspaceRoutes.SignUp}?${inviteSearch}`
            : `${PublicRoutes.SignUp}?${inviteSearch}`
        }
        replace
      />
    )
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()

    const passwordError = validatePassword(formInput.password, formInput.passwordRepeat)
    if (passwordError) {
      setError(te(`password.${passwordError}`))
    } else {
      handleCreateWorkspace()
    }
  }

  return (
    <>
      <div>
        <h1>{t('password.welcome')}</h1>
        <div className={accessStyles.message}>{t('password.message')}</div>
        <div className={styles.progress} aria-hidden="true">
          <span className={styles.currentStep} />
          <span className={styles.currentStep} />
          <span />
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <div className={accessStyles.inputs}>
          <Input
            autoComplete="new-password"
            autoFocus
            error={error ? ' ' : undefined}
            label={t('password.password')}
            mandatory
            onChange={(password) =>
              setFormInput((prev) => ({
                ...prev,
                password,
              }))
            }
            placeholder={t('password.placeholder.password')}
            type="password"
            value={formInput.password}
          />
          <Input
            autoComplete="new-password"
            error={error}
            label={t('password.passwordRepeat')}
            mandatory
            onChange={(passwordRepeat) =>
              setFormInput((prev) => ({
                ...prev,
                passwordRepeat,
              }))
            }
            placeholder={t('password.placeholder.passwordRepeat')}
            type="password"
            value={formInput.passwordRepeat}
          />
        </div>
        <div className={styles.actions}>
          <Button
            leftIcon={<ArrowLeftIcon />}
            onClick={() => void navigate(-1)}
            variant="secondary"
          >
            {t('password.back')}
          </Button>
          <Button
            disabled={!formInput.password || !formInput.passwordRepeat}
            type="submit"
            rightIcon={<ArrowRightIcon />}
          >
            {t(isCreateWorkspace ? 'workspaceSetup.create' : 'password.create')}
          </Button>
        </div>
      </form>
    </>
  )
}

export default SetupPasswordPage
